import {useState} from "react";
import {useMessages} from "i18n";
import {CardSheet} from "components/CardSheet";
import {SegmentedTabs} from "components/SegmentedTabs";
import {useUser} from "UserProvider";
import {canUseCustomDomain, canUseCustomerBranding, planOf} from "home/gating";
import SubdomainSettings from "../subdomain/SubdomainSettings";
import CustomDomainSettings from "../customDomain/CustomDomainSettings";
import {Section, SectionTitle, TabBar, TabPanel} from "./BrandSheet.styles";
import BrandSettings from "./BrandSettings";
import {PageThemeSection} from "./PageThemeSection";
import {
	defaultPersonalizationTab,
	personalizationTabs,
	personalizationUpsellTab,
	type PersonalizationTabId,
} from "./personalizationTabs";

/**
 * Everything that decides what a recipient sees, as one card sheet — what the top bar's
 * "Personalization" item opens (ui#450/ui#457). Three TABS: the logo/accent editor, the download-page theme
 * (api#511) and the address, which had an account → Profile card of its own until the owner said "put it
 * under branding" and gave every brand-identity editor this one home (ui#529).
 *
 * The address tab holds TWO sections, because "where do my links live" has two answers: a claimed label
 * under one of our zones (ui#529) and a domain the sender already owns (ui#669). They share the
 * `customDomain` plan capability and the api's custom-domain table, but they are different features
 * with different setup — the claim needs no proof, the BYO domain needs a DNS challenge and a certificate.
 *
 * Since ui#648 those areas are TABS rather than three sections stacked in one scrolling window, which
 * is what the owner asked for ("a wider dialog with 3 tabs or nicer presentation than a scrolling
 * window"). Three things about that are worth stating, because each looks like a decision that could
 * have gone the other way:
 *
 * 1. It is still ONE home. ui#495's "single home" rule is about surfaces a sender has to go looking
 *    for — a second card on the account page — not about panels inside one sheet. There is still one
 *    top-bar item, one sheet, one overlay; only the arrangement inside it changed. (That overlay owns
 *    a URL of its own since ui#1292 — `/personalization` — which changed nothing about this sheet's
 *    contents or its one-home rule, only who decides it is open.)
 * 2. It is a SHEET, never a centred dialog. "Wider" is served by the card's 600px — the width the
 *    side sheet's `wide` size had — and by showing one area at a time instead of three.
 * 3. Which tabs exist is a RULE, not markup — a sender without `customerLogoAndColors` never had a
 *    theme section, so they must not get a theme tab onto nothing. It lives in
 *    {@link ./personalizationTabs}, and {@link ./brandSheetUpsell} still decides whose upsell shows.
 *
 * An almost-dumb frame (epic #26): it holds the active tab and reads two plan selectors, and every
 * other gate stays where it was, inside each editor's own hook. The selectors are pure functions of
 * the session's plan, NOT a second `GetBrand` fetch: the editors' hooks own their requests, and
 * re-running one here would double them on every open.
 *
 * The frame is the shared NON-MODAL {@link CardSheet} (the ui#1054 presentation every other sheet
 * moved onto), not the modal side sheet ui#457 shipped. That is a behaviour change before a visual
 * one, and it is the point of the move: the top bar this sheet is raised FROM stays in the
 * accessibility tree and stays clickable, because there is no scrim, no portal and no focus trap over
 * it. From the primitive it inherits Escape-to-close (yielding to any genuinely modal dialog opened
 * inside it, such as a template preview), the deferred exit transition, the band claim that closes
 * whichever other card sheet was open, and focus restoration to the opener it is handed. Below 640px
 * the card goes full-bleed under the bar, where the tab strip scrolls sideways rather than wrapping
 * (see {@link TabBar}).
 *
 * `role="dialog"` WITHOUT `aria-modal`, which is exactly what ARIA calls a non-modal dialog. It stayed
 * a dialog when ui#1292 gave the surface a route: what makes the destination sheet a `region` is that it
 * is a route-scoped DESTINATION, whereas this is still a transient editor a sender raises, works in and
 * dismisses — owning a URL so it can be linked and refreshed did not turn it into somewhere you go.
 *
 * The sheet carries NO actions of its own: each editor already owns its Save and reset, and the
 * header ✕ closes.
 *
 * Mounted at the {@link NavBar} top level like the other bar-opened overlays. A closed card sheet
 * renders nothing at all, so the sections' `GetBrand`, `GetSubdomainClaim` and
 * `GetCustomDomain` only run once the sender actually opens it. Inside an OPEN sheet the
 * inactive tabs stay mounted on purpose — see {@link TabPanel} for why switching tabs must not
 * refetch or discard an unsaved edit.
 */
export type BrandSheetProps = {
	open: boolean;
	onClose: () => void;
	/**
	 * Where focus goes when the sheet closes. The bar supplies it because Branding has TWO openers
	 * since ui#451 — the pill button, which persists, and a ProfileMenu row, which is gone by the time
	 * the sheet closes — so the caller, not the sheet, decides which element is the real destination.
	 */
	returnFocus?: HTMLElement | null;
	/** Resolves the durable bar trigger if it was not committed when the sheet first rendered. */
	returnFocusFallback?: () => HTMLElement | null;
	/** Runs once the exit transition has finished, so the bar can release its captured opener. */
	onExited?: () => void;
};

export function BrandSheet({
	open, onClose, returnFocus, returnFocusFallback, onExited,
}: BrandSheetProps) {
	const {account, common} = useMessages();
	const [user] = useUser();
	const plan = planOf(user);
	const entitlements = {
		brandEntitled: canUseCustomerBranding(plan),
		subdomainEntitled: canUseCustomDomain(plan),
	};
	const tabs = personalizationTabs(entitlements);
	const upsellTab = personalizationUpsellTab(entitlements);
	const [tab, setTab] = useState<PersonalizationTabId>(() => defaultPersonalizationTab(entitlements));
	// A plan can change under an open sheet (an upgrade in another tab refreshes the session), which can
	// retire the tab being viewed. Fall back to the default rather than rendering an empty sheet.
	const active = tabs.includes(tab) ? tab : defaultPersonalizationTab(entitlements);

	return (
		<CardSheet
			open={open}
			onClose={onClose}
			onExited={onExited}
			returnFocus={returnFocus}
			returnFocusFallback={returnFocusFallback}
			title={account.personalization.title}
			titleAccent={account.personalization.titleAccent}
			subtitle={account.personalization.subtitle}
			closeLabel={common.close}
			role="dialog"
			// The ONE sheet that keeps a hard scroll edge (ui#1281), because it does not have one: the
			// sticky {@link ./BrandSheet.styles} `TabBar` below is pinned flush against that boundary and
			// paints an opaque `card` fill across it, so content scrolls UNDER the strip and is hidden
			// rather than sliced. Fading the scroll region here would dissolve the strip itself — the
			// only thing doing that hiding — which is a worse version of the defect the fade exists for.
			fadeScrollEdge={false}
		>
			<TabBar>
				<SegmentedTabs
					ariaLabel={account.personalization.tabsAria}
					value={active}
					onChange={setTab}
					options={tabs.map(id => ({value: id, label: account.personalization.tabs[id]}))}
				/>
			</TabBar>

			<TabPanel hidden={active !== "brand"}>
				<BrandSettings showLockedNote={upsellTab === "brand"}/>
			</TabPanel>

			{tabs.includes("theme") && (
				<TabPanel hidden={active !== "theme"}>
					<PageThemeSection/>
				</TabPanel>
			)}

			<TabPanel hidden={active !== "domain"}>
				<Section>
					<SectionTitle>{account.subdomain.title}</SectionTitle>
					<SubdomainSettings showLockedNote={upsellTab === "domain"}/>
				</Section>
				{/*
				 * Bring-your-own domain (ui#669), the second half of "where do my links live". It takes no
				 * `showLockedNote`: the sheet's ONE upsell sentence is already rendered above by the section
				 * that owns it, and both are gated on the same `customDomain` capability, so a second copy
				 * would be noise for the sender and a strict-mode failure for every spec resolving it.
				 */}
				<Section>
					<SectionTitle>{account.customDomain.title}</SectionTitle>
					<CustomDomainSettings/>
				</Section>
			</TabPanel>
		</CardSheet>
	);
}
